export default () => {
  const apiRequest = useApiRequest();

  const { getAllVehicles, createVehicle, getVehicle, updateVehicle, deleteVehicle } = useApiVehicle();
  const { getAllInvolvedPersons, createInvolvedPerson, getInvolvedPerson, updateInvolvedPerson, deleteInvolvedPerson } = useApiInvolvedPerson();

  const investigationBaseUrl = 'reports/ir/';
  const supplementalBaseUrl = 'reports/ir/';

  return {
    getAllVehicles,
    createVehicle,
    getVehicle,
    updateVehicle,
    deleteVehicle,
    getAllInvolvedPersons,
    createInvolvedPerson,
    getInvolvedPerson,
    updateInvolvedPerson,
    deleteInvolvedPerson,
    /* -------------------------------------------------------------------------- */
    /*                                    Apps                                    */
    /* -------------------------------------------------------------------------- */

    /**
     * Gets all the server "apps" for the logged-in user.
     */
    getAllAppsForUser: () => apiRequest.get<Page<AppConfig>>('apps/'),

    /**
     * Gets all the roles for the server "apps".
     */
    getAllRoles: () => apiRequest.get<Page<Role>>('apps/role/'),

    /**
     * Gets a specific role for a server "app".
     *
     * @param id
     */
    getRole: (id: number) => apiRequest.get<Role>(`apps/role/${id}/`),

    /**
     * Gets all the permissions for the server, "apps" and otherwise.
     */
    getAllPermissions: () => apiRequest.get<Page<Permission>>('apps/permission/'),

    /* -------------------------------------------------------------------------- */
    /*                                 Signatures                                 */
    /* -------------------------------------------------------------------------- */

    /**
     * Submits data for the signature generator form and return a signature.
     *
     * @param formData
     */
    generateSignature: (formData: GenericFormData) =>
      apiRequest.post<Signature>('signature/', { body: formData }),

    /* -------------------------------------------------------------------------- */
    /*                                  Incidents                                 */
    /* -------------------------------------------------------------------------- */

    /**
     * Gets all incidents, but without related reports.
     * @see `internal.getAllIncidents` for retrieving incidents _with_ their
     * related reports.
     */
    getAllIncidents: (options: IncidentFilters = {}) => apiRequest.get<Page<Incident>>('reports/incident/', { query: options }),

    /**
     * Creates a new incident
     *
     * @param data
     */
    createIncident: (data: Incident) =>
      apiRequest.post<Incident>('reports/incident/', { body: data }),

    /**
     * Updates a specific incident
     *
     * @param id
     * @param data
     */
    updateIncident: (id: number, data: Partial<Incident>) =>
      apiRequest.patch<Incident>(`reports/incident/${id}/`, { body: data }),

    /* -------------------------------------------------------------------------- */
    /*                        Investigation Reports                               */
    /* -------------------------------------------------------------------------- */

    /**
     * Gets a list of investigation reports, potentially filtered.
     *
     * @param filters
     */
    getAllInvestigationReports: (options: InvestigationReportPageFilters = {}) =>
      apiRequest.get<Page<InvestigationReport>>(investigationBaseUrl, { query: { ...options, supplemental: false } }),

    /**
     * Creates a new investigation report
     *
     * @param report
     */
    createInvestigationReport: (report: Partial<InvestigationReportChanges>) =>
      apiRequest.post<InvestigationReport>(investigationBaseUrl, { body: report }),

    /**
     * Get a specific investigation report
     *
     * @param id
     */
    getInvestigationReport: (id: number) =>
      apiRequest.get<InvestigationReport>(`${investigationBaseUrl}${id}/`),
    getInvestigationReportPdf: (id: number) =>
      apiRequest.get(`${investigationBaseUrl}${id}/pdf`),

    searchByInvestigationReport: (id: number, params: any) =>
      apiRequest.get<any>(`${investigationBaseUrl}${id}/search`, { params }), // api/1.0/ir/{id}/search?involved_persons=True&vehicles=True
    searchInvestigationReport: (params: any) =>
      apiRequest.get<Page<InvestigationReport>>(investigationBaseUrl, { params }),

    /**
     * Updates a specific investigation report
     *
     * @param id
     */
    updateInvestigationReport: (id: number, data: Partial<InvestigationReportChanges>) =>
      apiRequest.patch<InvestigationReport>(`${investigationBaseUrl}${id}/`, { body: data }),

    /**
     * Updates the status of a specific investigation report
     *
     * @param id
     * @param status
     */
    updateInvestigationReportStatus: (id: number, data: StatusChange) =>
      apiRequest.patch<Status>(`${investigationBaseUrl}${id}/status/`, { body: data }),

    /**
     * Delete a specific investigation report.
     *
     * @param id
     */
    deleteInvestigationReport: (id: number) =>
      apiRequest.delete<InvestigationReport>(`${investigationBaseUrl}${id}/`),

    /* -------------------------------------------------------------------------- */
    /*                        Supplemental Reports                                */
    /* -------------------------------------------------------------------------- */
    /**
     * Gets a list of supplemental reports, potentially filtered.
     *
     * @param filters
     */
    getAllSupplementalReports: (options: SupplementalReportPageFilters = {}) =>
      apiRequest.get<Page<SupplementalReport>>(supplementalBaseUrl, { query: { ...options, supplemental: true } }),

    /**
     * Creates a new SupplementalReport
     *
     * @param report
     */
    createSupplementalReport: (report: Partial<SupplementalReportChanges>) =>
      apiRequest.post<SupplementalReport>(supplementalBaseUrl, { body: report }),

    /**
     * Get a specific SupplementalReport
     *
     * @param id
     */
    getSupplementalReport: (id: number) =>
      apiRequest.get<SupplementalReport>(`${supplementalBaseUrl}${id}/`),
    getSupplementalReportPdf: (id: number) =>
      apiRequest.get(`${supplementalBaseUrl}${id}/pdf`),

    /**
     * Updates a specific SupplementalReport
     *
     * @param id
     */
    updateSupplementalReport: (id: number, data: Partial<SupplementalReportChanges>) =>
      apiRequest.patch<SupplementalReport>(`${supplementalBaseUrl}${id}/`, { body: data }),

    /**
     * Updates the status of a specific SupplementalReport
     *
     * @param id
     * @param status
     */
    updateSupplementalReportStatus: (id: number, data: StatusChange) =>
      apiRequest.patch<Status>(`${supplementalBaseUrl}${id}/status/`, { body: data }),

    /**
     * Delete a specific SupplementalReport.
     *
     * @param id
     */
    deleteSupplementalReport: (id: number) =>
      apiRequest.delete<SupplementalReport>(`${supplementalBaseUrl}${id}/`),

    /* -------------------------------------------------------------------------- */
    /*                        Property Sheets                                     */
    /* -------------------------------------------------------------------------- */

    /**
     * Gets a list of property sheets, potentially filtered.
     *
     * @param filters
     */
    getAllPropertySheets: (options: PropertySheetPageFilters = {}) =>
      apiRequest.get<Page<PropertySheet>>('reports/propertysheet/', { query: options }),

    /**
     * Creates a new investigation report
     *
     * @param report
     */
    createPropertySheet: (report: Partial<PropertySheetChanges>) =>
      apiRequest.post<PropertySheet>('reports/propertysheet/', { body: report }),

    /**
     * Get a specific investigation report
     *
     * @param id
     */
    getPropertySheet: (id: number) =>
      apiRequest.get<PropertySheet>(`reports/propertysheet/${id}/`),
    getPropertySheetPdf: (id: number) =>
      apiRequest.get(`reports/propertysheet/${id}/pdf/`),

    /**
     * Updates a specific investigation report
     *
     * @param id
     */
    updatePropertySheet: (id: number, data: Partial<PropertySheetChanges>) =>
      apiRequest.patch<PropertySheet>(`reports/propertysheet/${id}/`, { body: data }),

    /**
     * Updates the status of a specific property sheet
     *
     * @param id
     * @param status
     */
    updatePropertySheetStatus: (id: number, data: StatusChange) =>
      apiRequest.patch<Status>(`reports/propertysheet/${id}/status/`, { body: data }),

    /**
     * Delete a specific property sheet.
     *
     * @param id
     */
    deletePropertySheet: (id: number) =>
      apiRequest.delete<PropertySheet>(`reports/propertysheet/${id}/`),

    /* -------------------------------------------------------------------------- */
    /*                                Media Assets                                */
    /* -------------------------------------------------------------------------- */

    /**
     * Uploads a file as a media asset to be attached to a report.
     *
     * @param data Form data for the media asset, the following fields are expected:
     *   - "asset_type": 'image' | 'video'
     */
    getMediaAssetBlob: async (id: number) =>
      apiRequest.get<Blob>(`reports/media/${id}/file/`),
    createMediaAsset: (data: FormData) =>
      apiRequest.post<MediaAsset>('reports/media/', { body: data }),
    updateMediaAsset: (id: number, data: Partial<MediaAsset>) =>
      apiRequest.patch<MediaAsset>(`reports/media/${id}/`, { body: data }),
    deleteMediaAsset: (id: number) =>
      apiRequest.delete<MediaAsset>(`reports/media/${id}/`),

    /* -------------------------------------------------------------------------- */
    /*                               Incident Types                               */
    /* -------------------------------------------------------------------------- */

    /**
     * Gets all the incident types
     */
    getAllIncidentTypes: () =>
      apiRequest.get<Page<IncidentType>>('reports/incidenttype/'),

    /* -------------------------------------------------------------------------- */
    /*                                  Resources                                 */
    /* -------------------------------------------------------------------------- */

    /**
     * Gets all the resources for After Action Reports
     */
    getAllReportResources: () =>
      apiRequest.get<Page<ReportResource>>('reports/resource/'),

    /* -------------------------------------------------------------------------- */
    /*                                    Users                                   */
    /* -------------------------------------------------------------------------- */

    /**
     * Gets all Users
     *
     * @param filters
     */
    getUsers: (filters?: PageFilters) => apiRequest.get<Page<User>>('users/users/', { query: filters }),

    /**
     * Creates a new user
     *
     * @param user The user data to create
     */
    createUser: (user: User) =>
      apiRequest.post<User>('users/users/', { body: user }),

    getInvestigators: (filters?: PageFilters) => apiRequest.get<Page<User>>('users/users/?role__name=Investigator', { query: filters }),
    /**
     * Update a specific user's profile data
     *
     * @param id
     */
    updateUser: (id: number, user: User) => apiRequest.patch(`users/users/${id}/`, { body: user }),

    /**
     * Update a specific user's roles
     *
     * @param id
     * @param roles
     */
    updateUserRoles: (id: number, roles: number[]) => apiRequest.patch(`users/users/${id}/roles/`, { body: { roles } }),

    /**
     * Update a specific user's groups
     *
     * @param id
     * @param groups
     */
    updateUserGroups: (id: number, groups: number[]) => apiRequest.patch(`users/users/${id}/groups/`, { body: { groups } }),

    /* -------------------------------------------------------------------------- */
    /*                             User Notifications                             */
    /* -------------------------------------------------------------------------- */

    getAllNotifications: () => apiRequest.get<Page<UserNotification>>('notifications/'),

    updateNotification: (id: number, isRead: boolean) =>
      apiRequest.patch(`notifications/${id}/`, { body: { is_read: isRead } }),

    markAllNotificationsAsRead: (data: { before: string }) =>
      apiRequest.patch<{ count: number }>('notifications/read_all', { body: data }),

    /* -------------------------------------------------------------------------- */
    /*                                   V2 APIs                                  */
    /* -------------------------------------------------------------------------- */
    v2: {
      /* --------------------------------- Groups --------------------------------- */
      getAllGroups: () => apiRequest.get<Page<Group>>('groups/', { version: 'v2' }),

      /* ------------------------------ Notifications ----------------------------- */
      getAllBannerNotifications: (filters?: PageFilters) =>
        apiRequest.get<Page<BannerNotification>>('banner_notifications/', { version: 'v2', query: filters }),

      getActiveBannerNotifications: () =>
        apiRequest.get<Page<BannerNotification>>('banner_notifications/active/', { version: 'v2' }),

      createBannerNotification: (data: Omit<BannerNotification, 'id'>) =>
        apiRequest.post('banner_notifications/', { version: 'v2', body: data }),

      updateBannerNotification: (id: number, data: Partial<BannerNotification>) =>
        apiRequest.patch(`banner_notifications/${id}/`, { version: 'v2', body: data }),
    },

    /* -------------------------------------------------------------------------- */
    /*                                Internal APIs                               */
    /* -------------------------------------------------------------------------- */
    internal: {
      /**
       * Takes a list of Google Maps Static Map API request urls and returns them with a signature
       * @param urls
       */
      getSignedMapsUrls: (urls: string[]) => $fetch<string[]>('/api/static-maps/sign-urls', { method: 'POST', body: urls }),

      /**
       * Returns an object with the counts of After Action Reports for each status, specifically
       * for ones that the current user can view.
       */
      getAfterActionReportCounts: () => {
        const headers = useRequestHeaders(['cookie']);
        return $fetch('/api/reports/aar/counts', { headers });
      },

      /**
       * Returns an object with the counts of Significant Incident Reports for each status,
       * specifically for ones that the current user can view.
       */
      getSignificantIncidentReportCounts: () => {
        const headers = useRequestHeaders(['cookie']);
        return $fetch('/api/reports/sir/counts', { headers });
      },

      /**
       * Returns a list of the incidents _with_ their related reports
       */
      getAllIncidentsWithReports: (options: IncidentFilters = {}) => {
        const headers = useRequestHeaders(['cookie']);
        return $fetch<Incident[]>('/api/reports/incidents', { headers, query: options });
      },

      /**
         * Returns an object with the counts of Incidents for each status
         */
      getIncidentsCounts: () => {
        const headers = useRequestHeaders(['cookie']);
        // return { open: 0, closed: 0 };
        return $fetch('/api/reports/incidentreport/counts', { headers });
      },

      /**
       * Returns an object with the counts of Supplemental Reports for each status, specifically
       * for ones that the current user can view.
       */
      getSupplementalReportCounts: () => {
        const headers = useRequestHeaders(['cookie']);
        // return { published: 0, rejected: 0, reviewable: 0, approved: 0, draft: 0 };
        return $fetch('/api/reports/supplementalreport/counts', { headers });
      },

      /**
       * Returns an object with the counts of PropertySheet Reports for each status, specifically
       * for ones that the current user can view.
       */
      getPropertySheetCounts: () => {
        const headers = useRequestHeaders(['cookie']);
        // return { published: 0, rejected: 0, reviewable: 0, approved: 0, draft: 0 };
        return $fetch('/api/reports/propertysheet/counts', { headers });
      },

      /**
       * Returns an object with the counts of Investigation Reports for each status, specifically
       * for ones that the current user can view.
       */
      getInvestigationReportCounts: () => {
        const headers = useRequestHeaders(['cookie']);
        // return { published: 0, rejected: 0, reviewable: 0, approved: 0, draft: 0 };
        return $fetch('/api/reports/investigationreport/counts', { headers });
      },
    },
  };
};
